import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { NgbModal, NgbModalOptions, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { ToasterService } from "../../services/toaster.service";
import { CountryService } from "../../services/country.service";
import { Helper } from "src/app/core/helpers/helper";

@Component({
  selector: "app-book-for-other-modal",
  templateUrl: "./book-for-other-modal.component.html",
  styleUrl: "./book-for-other-modal.component.scss",
  standalone: false
})
export class BookForOtherModalComponent {
  @Input() title: string = "";
  isChangePassword!: boolean;

  updateType!: number;

  profileDetails: any;
  selectedPhoneCode: any;

  otherUserDetaisForm!: FormGroup;

  bookForOtherModalRef!: NgbModalRef;

  config: NgbModalOptions = { size: "md", centered: true, backdrop: "static" };

  @Output() otherCustomerAddEvent = new EventEmitter();
  @ViewChild("bookForOtherModal", { static: true }) bookForOtherModal!: TemplateRef<any>;

  constructor(public helper: Helper, private modalService: NgbModal, private toasterService: ToasterService, private countryService: CountryService) {}

  /**
   * Fetches the list of countries from the CountryService.
   * Returns a promise that resolves when the country list is successfully fetched.
   *
   * @returns A promise that resolves when the country list is fetched.
   */
  getCountryList(): Promise<void> {
    return new Promise((resolve) => {
      this.countryService.getCountryList({}).subscribe(() => {
        resolve();
      });
    });
  }

  /**
   * Opens the modal for booking for another user.
   * If the user's country phone code list is empty, fetches the country list first.
   * Initializes the form and opens the modal.
   *
   * @returns A promise that resolves when the modal is opened.
   */
  async openModal(): Promise<void> {
    if (this.helper.userCountryPhoneCodeList.length === 0) {
      await this.getCountryList();
    }
    this.selectedPhoneCode = this.helper.dispatcherDetails?.entity?.countryPhoneCode;
    this.initForm();

    this.bookForOtherModalRef = this.modalService.open(this.bookForOtherModal, this.config);

    this.bookForOtherModalRef.result.then(
      () => {},
      () => {
        this.otherCustomerAddEvent.emit(this.otherUserDetaisForm.value);
      }
    );
  }

  /**
   * Initializes the form for entering other user details.
   * Sets up form controls with validation rules.
   */
  initForm(): void {
    this.otherUserDetaisForm = new FormGroup<any>({
      firstName: new FormControl<string>("", [Validators.required]),
      lastName: new FormControl(""),
      email: new FormControl<string>("", [Validators.pattern(this.helper.emailRegex)]),
      countryPhoneCode: new FormControl<string>("", Validators.required),
      phone: new FormControl<number | null>(null, [
        Validators.required,
        Validators.minLength(this.helper.minPhoneNumberLength),
        Validators.maxLength(this.helper.maxPhoneNumberLength)
      ])
    });
  }

  /**
   * Adds another user by validating and submitting the form.
   * Trims whitespace from form values, sets the country phone code, and checks for form validity.
   * If valid, saves the form data to session storage and closes the modal.
   */
  addOtherUser(): void {
    Object.keys(this.otherUserDetaisForm.value).forEach((key) => {
      const value = this.otherUserDetaisForm.value[key];
      if (typeof value === "string") {
        this.otherUserDetaisForm.controls[key].setValue(value.trim());
      }
    });

    this.otherUserDetaisForm.patchValue({
      countryPhoneCode: this.selectedPhoneCode
    });

    if (this.otherUserDetaisForm.invalid) {
      this.otherUserDetaisForm.markAllAsTouched();
      return;
    }
    if (this.otherUserDetaisForm.value.phone === this.helper.dispatcherDetails?.entity?.phone) {
      this.toasterService.showNotification("error", this.helper.translateService.instant("validation-title.phone-number-cannot-be-the-same-as-your-default-phone-number"));
      return;
    }

    sessionStorage.setItem("otherUserDetails", JSON.stringify(this.otherUserDetaisForm.value));

    // Get existing users from sessionStorage
    const existingUsers = JSON.parse(sessionStorage.getItem("otherUsers") || "[]");

    // Add new user
    existingUsers.push(this.otherUserDetaisForm.value);
    sessionStorage.setItem("selectedUser", JSON.stringify(this.otherUserDetaisForm.value));
    // Save updated users list
    sessionStorage.setItem("otherUsers", JSON.stringify(existingUsers));
    this.closeModal();
  }

  /**
   * Closes the modal and emits an event indicating that another customer has been added.
   */
  closeModal(): void {
    this.bookForOtherModalRef.close();
    this.otherCustomerAddEvent.emit(this.otherUserDetaisForm.value);
  }

  get firstNameInput(): any {
    return this.otherUserDetaisForm.get("firstName");
  }

  get lastNameInput(): any {
    return this.otherUserDetaisForm.get("lastName");
  }

  get emailInput(): any {
    return this.otherUserDetaisForm.get("email");
  }

  get phoneInput(): any {
    return this.otherUserDetaisForm.get("phone");
  }
}
